import * as React from 'react';
import { FixMeLater } from '../../utils';
import { barSpacing, barWidth, labelClearance, pixelsPerDegree } from './TemperatureChart.constants';
import { TemperatureChartContext } from './TemperatureChart.context';
import { Temperatures } from './TemperatureChart.types';
import { useCulture } from '../../culture';

export type TemperatureChartMonthProps = {
    /**
     * If `true`, this month will be active.
     * @default false
     */
    active?: boolean;

    /**
     * @ignore
     */
    index?: number;

    /**
     * The temperature values for this month.
     */
    temperatures: Temperatures;

    /**
     * The name of this month.
     */
    monthName: string;

    /**
     * Callback fired when this month is hovered or touched.
     * @param {React.SyntheticEvent} event The event source of the callback.
     * @param {index} event The index of the new month.
     */
    onMouseEnter?: (event: React.SyntheticEvent, index: number) => void;
};

export const TemperatureChartMonth: React.ForwardRefExoticComponent<
    TemperatureChartMonthProps & React.RefAttributes<HTMLElement>
> = React.forwardRef(function TemperatureChartMonth(
    props: TemperatureChartMonthProps,
    ref: React.ForwardedRef<FixMeLater>
): React.JSX.Element {
    const { active = false, index = 0, monthName, onMouseEnter, temperatures, ...other } = props;

    const { dir } = useCulture();
    const isRTL = dir === 'rtl';

    const handleMouseEnter = (event: React.SyntheticEvent) => {
        onMouseEnter?.(event, index);
    };

    const { barBottom = 0, nameY, svgWidth = 0 } = React.useContext(TemperatureChartContext);

    const initial = monthName.substring(0, 1).toUpperCase();
    const height = temperatures.avg * pixelsPerDegree;

    // RTL-aware positions
    const barX = isRTL
        ? svgWidth - (barWidth + barSpacing) * (index + 1) + barSpacing
        : (barWidth + barSpacing) * index;
    const textX = barX + barWidth / 2;

    const barY = barBottom - height;
    const tempY = barY - labelClearance;

    return (
        <g
            ref={ref}
            role="listitem"
            onMouseEnter={handleMouseEnter}
            data-temperature-chart-month={true}
            data-active={active || undefined}
            {...other}
        >
            <rect width={barWidth} height={height} x={barX} y={barY} role="presentation" tabIndex={-1} rx="6" />
            <text x={textX} y={tempY} aria-label={`Average temperature in ${monthName}`}>
                {temperatures.avg}
            </text>
            <text x={textX} y={nameY} role="presentation">
                {initial}
            </text>
        </g>
    );
});
